import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Bold, Code, Heading2, Heading3, Italic, Link2, List, ListOrdered, Pilcrow, Table, Underline, Undo2, Redo2, Eraser } from "lucide-react";
import { cn } from "@/lib/utils";

const TABLE = '<table style="width:100%;border-collapse:collapse" border="1"><thead><tr><th>Heading</th><th>Heading</th></tr></thead><tbody><tr><td>Cell</td><td>Cell</td></tr><tr><td>Cell</td><td>Cell</td></tr></tbody></table><p></p>';

export const RichEditor = forwardRef(({ value, onChange, testId = "rich-editor" }, ref) => {
  const el = useRef(null);
  const [source, setSource] = useState(false);
  const range = useRef(null);

  useEffect(() => {
    if (el.current && !source && el.current.innerHTML !== value) el.current.innerHTML = value || "";
  }, [value, source]);

  const save = () => {
    const s = window.getSelection();
    if (s?.rangeCount && el.current?.contains(s.anchorNode)) range.current = s.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    el.current?.focus();
    if (range.current) {
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(range.current);
    }
  };
  const emit = () => onChange(el.current.innerHTML);
  const cmd = (c, v) => { restore(); document.execCommand(c, false, v); emit(); save(); };

  useImperativeHandle(ref, () => ({
    insertText: (t) => {
      if (source) return onChange((value || "") + t);
      cmd("insertText", t);
    },
  }));

  const tools = [
    [Pilcrow, "Paragraph", () => cmd("formatBlock", "<p>")], [Heading2, "Heading 2", () => cmd("formatBlock", "<h2>")], [Heading3, "Heading 3", () => cmd("formatBlock", "<h3>")],
    [Bold, "Bold", () => cmd("bold")], [Italic, "Italic", () => cmd("italic")], [Underline, "Underline", () => cmd("underline")],
    [List, "Bullet list", () => cmd("insertUnorderedList")], [ListOrdered, "Numbered list", () => cmd("insertOrderedList")],
    [Link2, "Link", () => { const u = window.prompt("Link URL", "https://"); if (u) cmd("createLink", u); }],
    [Table, "Insert table", () => cmd("insertHTML", TABLE)], [Eraser, "Clear formatting", () => cmd("removeFormat")],
    [Undo2, "Undo", () => cmd("undo")], [Redo2, "Redo", () => cmd("redo")],
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-white" data-testid={testId}>
      <div className="flex flex-wrap gap-0.5 border-b border-line bg-page p-1.5">
        {tools.map(([I, label, fn]) => (
          <button key={label} type="button" title={label} aria-label={label} disabled={source} onMouseDown={(e) => e.preventDefault()} onClick={fn} className="grid h-8 w-8 place-items-center rounded-lg text-body hover:bg-white hover:text-ocean disabled:opacity-40" data-testid={`${testId}-tool-${label.toLowerCase().replace(/\s+/g, "-")}`}><I className="h-4 w-4" /></button>
        ))}
        <button type="button" onClick={() => setSource(!source)} className={cn("ml-auto flex items-center gap-1.5 rounded-lg px-2.5 text-xs font-bold", source ? "bg-indigo-brand text-white" : "text-body hover:bg-white")} data-testid={`${testId}-source-toggle`}><Code className="h-4 w-4" />HTML</button>
      </div>
      {source ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} className="h-[480px] w-full resize-y p-4 font-mono text-xs text-navy outline-none" data-testid={`${testId}-source`} />
      ) : (
        <div ref={el} contentEditable suppressContentEditableWarning onInput={emit} onKeyUp={save} onMouseUp={save} onBlur={save}
          className="legal-content min-h-[480px] max-w-none p-5 outline-none [&_table]:w-full [&_td]:border [&_td]:border-line [&_td]:p-2 [&_th]:border [&_th]:border-line [&_th]:bg-page [&_th]:p-2" data-testid={`${testId}-content`} />
      )}
    </div>
  );
});
RichEditor.displayName = "RichEditor";
